import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { aboutInfo, reviews } from '@/mocks/hotelData';

export default function AboutPage() {
  const navigate = useNavigate();
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormError('');

    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();

    if (honeypot) {
      setFormStatus('success');
      setTimeout(() => setFormStatus('idle'), 3000);
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9tguulca23r0tr1aiug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        if (msg.toLowerCase().includes('spam')) {
          setFormStatus('error');
          setFormError('提交失败，请稍后重试');
        } else {
          setFormStatus('error');
          setFormError(msg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="Organization" data={{
        name: aboutInfo.brandName,
        url: 'https://www.grandlisboa.com',
        description: aboutInfo.description,
        foundingDate: '2007',
        address: { '@type': 'PostalAddress', addressLocality: '澳门', streetAddress: aboutInfo.address },
        telephone: aboutInfo.phone,
        email: aboutInfo.email,
        sameAs: ['https://www.grandlisboa.com'],
      }} />
      <SchemaOrg type="WebPage" data={{
        name: `关于我们 - ${aboutInfo.brandName}`,
        description: aboutInfo.description,
        url: 'https://www.grandlisboa.com/about',
        isPartOf: { '@type': 'WebSite', name: aboutInfo.brandName, url: 'https://www.grandlisboa.com' },
      }} />
      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.grandlisboa.com' },
          { '@type': 'ListItem', position: 2, name: '关于我们' },
        ],
      }} />

      <header><Navbar /></header>

      <main className="pt-16 md:pt-18">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="pt-6">
              <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />
            </div>
            <div className="pb-8">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">关于我们</h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl">澳门地标 · 极致奢华</p>
            </div>
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
              <div>
                <span className="text-xs md:text-sm text-primary-600 tracking-widest uppercase font-medium">Brand Story</span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-2 mb-6">品牌介绍</h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">{aboutInfo.description}</p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">{aboutInfo.mission}</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {aboutInfo.stats.map((stat) => (
                    <div key={stat.label} className="text-center p-4 bg-background-100 rounded-xl">
                      <div className="font-heading text-xl md:text-2xl font-bold text-primary-600">{stat.value}</div>
                      <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative h-72 md:h-96 rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=grand%20lisboa%20hotel%20macau%20exterior%20architecture%20golden%20lotus%20shape%20at%20night%20with%20city%20lights%20reflection%20luxurious%20atmosphere%20dramatic%20lighting%20iconic%20landmark&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="新葡京酒店外观"
                  title="新葡京酒店 - 澳门地标建筑"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Reviews */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <span className="text-xs md:text-sm text-accent-500 tracking-widest uppercase font-medium">Guest Reviews</span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-2 mb-3">宾客口碑</h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto">
                来自全球宾客的真实评价，见证新葡京酒店的卓越品质
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {reviews.map((review) => (
                <div key={review.id} className="bg-background-50 rounded-xl border border-background-200/70 p-5 md:p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="text-sm font-medium text-primary-700">{review.name.charAt(0)}</span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-800">{review.name}</div>
                        <div className="text-xs text-foreground-400">{review.platform} · {review.date}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i key={i} className={`text-xs ${i < review.rating ? 'ri-star-fill text-primary-500' : 'ri-star-line text-foreground-300'}`}></i>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14">
              <div>
                <span className="text-xs md:text-sm text-secondary-600 tracking-widest uppercase font-medium">Contact Us</span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-2 mb-6">联系我们</h2>

                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 shrink-0">
                      <i className="ri-map-pin-2-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-800 mb-1">酒店地址</h4>
                      <p className="text-sm text-foreground-500">澳门半岛葡京路2-4号</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 shrink-0">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-800 mb-1">联系电话</h4>
                      <p className="text-sm text-foreground-500">{aboutInfo.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-800 mb-1">电子邮箱</h4>
                      <p className="text-sm text-foreground-500">{aboutInfo.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 shrink-0">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-800 mb-1">前台服务</h4>
                      <p className="text-sm text-foreground-500">24小时全天候服务</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
                <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-6">在线咨询</h3>

                {formStatus === 'success' ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                      <i className="ri-check-line text-2xl text-primary-600"></i>
                    </div>
                    <h4 className="font-heading text-lg font-semibold text-foreground-900 mb-2">提交成功</h4>
                    <p className="text-sm text-foreground-500">感谢您的咨询，我们的客服团队将在24小时内与您联系。</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="d9tguulca23r0tr1aiug">
                    <div>
                      <label htmlFor="ab_name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        type="text"
                        id="ab_name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors duration-200"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="ab_email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        type="email"
                        id="ab_email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors duration-200"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="ab_phone" className="block text-sm font-medium text-foreground-700 mb-1.5">电话</label>
                      <input
                        type="tel"
                        id="ab_phone"
                        name="phone"
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors duration-200"
                        placeholder="请输入您的电话（选填）"
                      />
                    </div>
                    <div>
                      <label htmlFor="ab_message" className="block text-sm font-medium text-foreground-700 mb-1.5">咨询内容</label>
                      <textarea
                        id="ab_message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors duration-200 resize-none"
                        placeholder="请输入您的咨询内容（不超过500字）"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-600">{formError}</p>
                    )}
                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 disabled:opacity-50 transition-all duration-200 whitespace-nowrap cursor-pointer text-sm"
                    >
                      {formStatus === 'submitting' ? '提交中...' : '提交咨询'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Map */}
        <section className="py-10 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="rounded-xl overflow-hidden h-64 md:h-80">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3694.8927690123456!2d113.5400!3d22.1899!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjLCsDExJzIzLjYiTiAxMTPCsDMyJzM4LjAiRQ!5e0!3m2!1szh-CN!2smo!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="新葡京酒店位置"
                className="rounded-xl"
              ></iframe>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}